import React, { useEffect, useState, useRef, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BriefcaseIcon, BuildingIcon, ClipboardListIcon, ContactIcon, PanelLeftOpenIcon, PackageIcon, PinIcon, PieChartIcon, SettingsIcon, TrendingUpIcon, UsersIcon, XIcon } from 'lucide-react';
import { NavigationSettingsPanel } from './NavigationSettingsPanel';
import type { NavigationGroup, NavigationItem, NavigationProfile, NavigationProfileConfig } from './navigationTypes';
const BRAND = '#C8102E';
const profiles: NavigationProfile[] = [{
  id: 'loan-officer',
  label: 'Loan Officer'
}, {
  id: 'processor',
  label: 'Processor'
}, {
  id: 'underwriter',
  label: 'Underwriter'
}, {
  id: 'sales-manager',
  label: 'Sales Manager'
}, {
  id: 'system-administrator',
  label: 'System Administrator'
}];
const baseGroups: NavigationGroup[] = [{
  id: 'objects',
  title: 'Objects',
  items: [{
    id: 'loans',
    label: 'Loans',
    icon: BriefcaseIcon,
    visible: true
  }, {
    id: 'leads',
    label: 'Leads',
    icon: UsersIcon,
    visible: true
  }, {
    id: 'contacts',
    label: 'Contacts',
    icon: ContactIcon,
    visible: true
  }, {
    id: 'accounts',
    label: 'Accounts',
    icon: BuildingIcon,
    visible: true
  }, {
    id: 'cases',
    label: 'Cases',
    icon: ClipboardListIcon,
    visible: true
  }]
}, {
  id: 'components',
  title: 'Components',
  items: [{
    id: 'active-pipeline',
    label: 'Active Pipeline',
    icon: TrendingUpIcon,
    visible: true
  }, {
    id: 'order-management',
    label: 'Order Management',
    icon: PackageIcon,
    visible: true
  }, {
    id: 'reports',
    label: 'Reports',
    icon: PieChartIcon,
    visible: true
  }]
}];
function cloneGroups(groups: NavigationGroup[]) {
  return groups.map(group => ({
    ...group,
    items: group.items.map(item => ({
      ...item
    }))
  }));
}
function configureProfile(visibleIds: string[], order: Record<string, string[]>, preferences: Pick<NavigationProfileConfig, 'compactRail' | 'keepPinned'>): NavigationProfileConfig {
  return {
    groups: baseGroups.map(group => {
      const orderedIds = order[group.id] ?? group.items.map(item => item.id);
      const items = orderedIds.map(id => group.items.find(item => item.id === id)).filter((item): item is NavigationItem => Boolean(item)).map(item => ({
        ...item,
        visible: visibleIds.includes(item.id)
      }));
      return {
        ...group,
        items
      };
    }),
    ...preferences
  };
}
const profileDefaults: Record<string, NavigationProfileConfig> = {
  'loan-officer': configureProfile(['leads', 'contacts', 'accounts', 'loans', 'active-pipeline', 'reports'], {
    objects: ['leads', 'contacts', 'accounts', 'loans', 'cases'],
    components: ['active-pipeline', 'reports', 'order-management']
  }, {
    compactRail: false,
    keepPinned: false
  }),
  processor: configureProfile(['loans', 'contacts', 'cases', 'accounts', 'order-management', 'reports'], {
    objects: ['loans', 'cases', 'contacts', 'accounts', 'leads'],
    components: ['order-management', 'reports', 'active-pipeline']
  }, {
    compactRail: true,
    keepPinned: false
  }),
  underwriter: configureProfile(['loans', 'cases', 'contacts', 'accounts', 'reports'], {
    objects: ['cases', 'loans', 'contacts', 'accounts', 'leads'],
    components: ['reports', 'active-pipeline', 'order-management']
  }, {
    compactRail: true,
    keepPinned: true
  }),
  'sales-manager': configureProfile(['leads', 'accounts', 'contacts', 'loans', 'active-pipeline', 'reports'], {
    objects: ['leads', 'accounts', 'contacts', 'loans', 'cases'],
    components: ['active-pipeline', 'reports', 'order-management']
  }, {
    compactRail: false,
    keepPinned: true
  }),
  'system-administrator': configureProfile(baseGroups.flatMap(group => group.items.map(item => item.id)), {}, {
    compactRail: false,
    keepPinned: false
  })
};
function getDefaultConfig(profileId: string): NavigationProfileConfig {
  const config = profileDefaults[profileId] ?? profileDefaults['loan-officer'];
  return {
    groups: cloneGroups(config.groups),
    compactRail: config.compactRail,
    keepPinned: config.keepPinned
  };
}
function getInitialConfigs() {
  return Object.fromEntries(profiles.map(profile => [profile.id, getDefaultConfig(profile.id)])) as Record<string, NavigationProfileConfig>;
}
export function SideDrawer() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [activeId, setActiveId] = useState('active-pipeline');
  const [selectedProfileId, setSelectedProfileId] = useState('loan-officer');
  const [profileConfigs, setProfileConfigs] = useState<Record<string, NavigationProfileConfig>>(getInitialConfigs);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedProfile = profiles.find(profile => profile.id === selectedProfileId) ?? profiles[0];
  const config = profileConfigs[selectedProfileId];
  const openPopover = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setIsPopoverOpen(true);
  };
  const schedulePopoverClose = () => {
    if (config.keepPinned) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setIsPopoverOpen(false);
    }, 150);
  };
  const keepOpenRailActive = () => {
    if (!isPopoverOpen || closeTimer.current === null) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const updateConfig = (update: (current: NavigationProfileConfig) => NavigationProfileConfig) => {
    setProfileConfigs(current => ({
      ...current,
      [selectedProfileId]: update(current[selectedProfileId])
    }));
  };
  const updateItem = (groupId: string, itemId: string, update: (item: NavigationItem) => NavigationItem) => {
    updateConfig(current => ({
      ...current,
      groups: current.groups.map(group => group.id === groupId ? {
        ...group,
        items: group.items.map(item => item.id === itemId ? update(item) : item)
      } : group)
    }));
  };
  const moveItem = (groupId: string, itemId: string, direction: 'up' | 'down') => {
    updateConfig(current => ({
      ...current,
      groups: current.groups.map(group => {
        if (group.id !== groupId) return group;
        const index = group.items.findIndex(item => item.id === itemId);
        const nextIndex = direction === 'up' ? index - 1 : index + 1;
        if (index < 0 || nextIndex < 0 || nextIndex >= group.items.length) return group;
        const items = [...group.items];
        [items[index], items[nextIndex]] = [items[nextIndex], items[index]];
        return {
          ...group,
          items
        };
      })
    }));
  };
  const addItem = (groupId: string, label: string, icon: NavigationItem['icon']) => {
    const id = `${groupId}-${Date.now()}`;
    updateConfig(current => ({
      ...current,
      groups: current.groups.map(group => group.id === groupId ? {
        ...group,
        items: [...group.items, {
          id,
          label,
          icon,
          visible: true
        }]
      } : group)
    }));
    setActiveId(id);
  };
  const removeItem = (groupId: string, itemId: string) => {
    const remainingVisibleItems = config.groups.flatMap(group => group.id === groupId ? group.items.filter(item => item.id !== itemId) : group.items).filter(item => item.visible);
    updateConfig(current => ({
      ...current,
      groups: current.groups.map(group => group.id === groupId ? {
        ...group,
        items: group.items.filter(item => item.id !== itemId)
      } : group)
    }));
    if (activeId === itemId) setActiveId(remainingVisibleItems[0]?.id ?? '');
  };
  const selectProfile = (profileId: string) => {
    const nextConfig = profileConfigs[profileId];
    const firstVisible = nextConfig.groups.flatMap(group => group.items).find(item => item.visible);
    setSelectedProfileId(profileId);
    if (firstVisible) setActiveId(firstVisible.id);
    setIsPopoverOpen(true);
  };
  const resetSelectedProfile = () => {
    const nextConfig = getDefaultConfig(selectedProfileId);
    const firstVisible = nextConfig.groups.flatMap(group => group.items).find(item => item.visible);
    setProfileConfigs(current => ({
      ...current,
      [selectedProfileId]: nextConfig
    }));
    if (firstVisible) setActiveId(firstVisible.id);
  };
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSettingsOpen) setIsPopoverOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [isSettingsOpen]);
  return <>
      <AnimatePresence data-id="element-92" data-mp="oxokkumk67xy">
        {isPopoverOpen && <motion.nav aria-label="Primary navigation" onMouseEnter={keepOpenRailActive} onMouseLeave={schedulePopoverClose} className="fixed inset-y-0 left-0 z-50 flex h-screen w-[min(7.25rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-r-2xl border-y border-r border-slate-700 bg-slate-900 text-slate-300 shadow-2xl" initial={{
        opacity: 0,
        x: -12,
        scale: 0.98
      }} animate={{
        opacity: 1,
        x: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: -12,
        scale: 0.98
      }} transition={{
        type: 'spring',
        stiffness: 360,
        damping: 28
      }} data-id="element-93" data-mp="tt3yfmkkg3ud">
            <header className="relative flex shrink-0 flex-col items-center border-b border-slate-800 px-2 py-3" data-id="element-94" data-mp="1jko378a6ky91">
              <span className="text-center text-[9px] font-bold uppercase leading-tight tracking-[0.12em] text-slate-400" data-id="element-95" data-mp="2v1xidima144j">
                {selectedProfile.label}
              </span>
              <span className="mt-1 text-[10px] font-semibold text-white" data-id="element-96" data-mp="357yvi9dggi9w">
                Navigation
              </span>
              <div className="mt-2 flex items-center gap-1" data-id="element-97" data-mp="2vft16szqdorj">
                <button type="button" onClick={() => updateConfig(current => ({
              ...current,
              keepPinned: !current.keepPinned
            }))} aria-pressed={config.keepPinned} aria-label={config.keepPinned ? 'Unpin navigation popover' : 'Keep navigation popover open'} title={config.keepPinned ? 'Unpin popover' : 'Pin popover open'} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" style={config.keepPinned ? {
              color: BRAND,
              backgroundColor: 'rgba(200,16,46,0.12)'
            } : undefined} data-id="element-98" data-mp="2ve852yxrpxrk">
                  <PinIcon className="h-3.5 w-3.5" data-id="element-99" data-mp="2ixyrz1fvx8ek" />
                </button>
                <button type="button" onClick={() => setIsPopoverOpen(false)} aria-label="Close navigation" className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]" data-id="element-100" data-mp="2ivw3iludrnby">
                  <XIcon className="h-3.5 w-3.5" data-id="element-101" data-mp="ygaodivj37bc" />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto py-4" data-id="element-102" data-mp="13evo7h6vboz9">
              {config.groups.map(group => {
            const visibleItems = group.items.filter(item => item.visible);
            if (!visibleItems.length) return null;
            return <section key={group.id} className="mb-4 last:mb-0" data-id="element-103" data-mp="14wsy24sebf75">
                    <h2 className="mb-2 px-2 text-center text-[9px] font-bold uppercase tracking-wider text-slate-500" data-id="element-104" data-mp="zyj2vu6o8elj">
                      {group.title}
                    </h2>
                    <div className="grid grid-cols-1 gap-1.5 px-2" data-id="element-105" data-mp="7adi57fmaplp">
                      {visibleItems.map(item => {
                  const Icon = item.icon;
                  const isActive = activeId === item.id;
                  return <button key={item.id} type="button" onClick={() => setActiveId(item.id)} aria-current={isActive ? 'page' : undefined} title={item.label} className={`relative flex min-h-[4.25rem] flex-col items-center justify-center rounded-xl border px-1.5 py-2 text-center outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${isActive ? 'text-white' : 'border-slate-700 bg-slate-800 hover:border-slate-600 hover:bg-slate-700'}`} style={isActive ? {
                    backgroundColor: BRAND,
                    borderColor: BRAND,
                    boxShadow: '0 8px 16px -8px rgba(200,16,46,0.5)'
                  } : undefined} data-id="element-106" data-mp="h78dxikiabwa">
                            <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${isActive ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'}`} data-id="element-107" data-mp="1qammhbykkxet">
                              <Icon className="h-4 w-4" data-id="element-108" data-mp="190s7hbbz2txc" />
                            </span>
                            <span className={`mt-1.5 line-clamp-2 text-[10px] leading-[0.75rem] ${isActive ? 'font-semibold' : 'font-medium'}`} data-id="element-109" data-mp="18yfr32lv4rme">
                              {item.label}
                            </span>
                          </button>;
                })}
                    </div>
                  </section>;
          })}
            </div>

            <footer className="shrink-0 border-t border-slate-800 p-2" data-id="element-110" data-mp="1np6kug70m0s5">
              <button type="button" onClick={() => {
            updateConfig(current => ({
              ...current,
              keepPinned: true
            }));
            setIsSettingsOpen(true);
          }} title="Setup & Settings" className="flex w-full flex-col items-center justify-center rounded-xl border border-slate-700 bg-slate-800 px-1.5 py-2 text-slate-300 transition-colors hover:border-slate-600 hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900" data-id="element-111" data-mp="h9w5x6vd4bpd">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-700" data-id="element-112" data-mp="3vxl5a9i87eho">
                  <SettingsIcon className="h-4 w-4" data-id="element-113" data-mp="3h96v1dihcnr3" />
                </span>
                <span className="mt-1 text-center text-[9px] font-semibold leading-tight" data-id="element-114" data-mp="3m78omsdj85gt">
                  Settings
                </span>
              </button>
            </footer>
          </motion.nav>}
      </AnimatePresence>

      <div className={`fixed bottom-5 left-5 ${isPopoverOpen ? 'z-40' : 'z-[60]'}`} data-id="element-115" data-mp="3d5il4o6dlg2o">
        <button type="button" aria-label="Open navigation" aria-expanded={isPopoverOpen} onClick={() => setIsPopoverOpen(open => !open)} onFocus={openPopover} onMouseEnter={openPopover} onMouseLeave={schedulePopoverClose} className="group flex h-14 w-14 items-center justify-center rounded-full border border-white/20 bg-slate-900 text-white shadow-lg shadow-slate-950/25 transition-all hover:scale-105 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:ring-offset-2" data-id="element-116" data-mp="2tltgcdn3m468">
          <PanelLeftOpenIcon className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" data-id="element-117" data-mp="3m8wbcvskfwrq" />
          <span className="sr-only" data-id="element-118" data-mp="3hcpgby1pk5ok">Navigation</span>
        </button>
      </div>

      <NavigationSettingsPanel isOpen={isSettingsOpen} groups={config.groups} profiles={profiles} selectedProfileId={selectedProfileId} compactRail={config.compactRail} keepPinned={config.keepPinned} onProfileChange={selectProfile} onClose={() => setIsSettingsOpen(false)} onSave={() => setIsSettingsOpen(false)} onReset={resetSelectedProfile} onToggleItem={(groupId, itemId) => updateItem(groupId, itemId, item => ({
      ...item,
      visible: !item.visible
    }))} onRenameItem={(groupId, itemId, label) => updateItem(groupId, itemId, item => ({
      ...item,
      label
    }))} onMoveItem={moveItem} onAddItem={addItem} onRemoveItem={removeItem} onCompactRailChange={compactRail => updateConfig(current => ({
      ...current,
      compactRail
    }))} onKeepPinnedChange={keepPinned => updateConfig(current => ({
      ...current,
      keepPinned
    }))} data-id="element-119" data-mp="3vax7fpc2ccrt" />
    </>;
}